import { api } from './client';
import type { AuthResponse, AuthUser } from '@/types/auth';

type Envelope<T> = { data: T };

export type RegisterResponse = AuthResponse | { pending: true; email: string };
export type LoginResponse = AuthResponse | { requires2Fa: true; challengeToken: string };

export const authApi = {
  async register(input: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName?: string;
  }): Promise<RegisterResponse> {
    const { data } = await api.post<Envelope<RegisterResponse>>('/auth/register', input);
    return data.data;
  },

  async login(input: { email: string; password: string; twoFaCode?: string }): Promise<LoginResponse> {
    const { data } = await api.post<Envelope<LoginResponse>>('/auth/login', input);
    return data.data;
  },

  /** Exchange an IsoSofts JWT for an ESD session (central auth, no password). */
  async sso(token: string): Promise<AuthResponse> {
    const { data } = await api.post<Envelope<AuthResponse>>('/auth/sso', { token });
    return data.data;
  },

  async verify2Fa(input: { challengeToken: string; code: string }): Promise<AuthResponse> {
    const { data } = await api.post<Envelope<AuthResponse>>('/auth/2fa/verify', input);
    return data.data;
  },

  async setup2Fa(): Promise<{ secret: string; otpauthUrl: string; qrDataUrl: string }> {
    const { data } = await api.post<Envelope<{ secret: string; otpauthUrl: string; qrDataUrl: string }>>('/auth/2fa/setup');
    return data.data;
  },

  async enable2Fa(code: string): Promise<{ enabled: true }> {
    const { data } = await api.post<Envelope<{ enabled: true }>>('/auth/2fa/enable', { code });
    return data.data;
  },

  async disable2Fa(code: string): Promise<{ enabled: false }> {
    const { data } = await api.post<Envelope<{ enabled: false }>>('/auth/2fa/disable', { code });
    return data.data;
  },

  async logout(refreshToken: string): Promise<void> {
    await api.post('/auth/logout', { refreshToken });
  },

  async me(): Promise<AuthUser> {
    const { data } = await api.get<Envelope<AuthUser>>('/auth/me');
    return data.data;
  },

  /**
   * Pull identity changes (email) made in IsoSofts onto this ESD session and return
   * the refreshed profile. IsoSofts does not invalidate the session when an admin
   * edits a staff email, so the SSO exchange alone would leave the old address in
   * place until the next real login.
   */
  async syncIdentity(token: string): Promise<AuthUser> {
    const { data } = await api.post<Envelope<AuthUser>>('/auth/sync-identity', { token });
    return data.data;
  },

  async verifyEmail(token: string): Promise<{ verified: boolean }> {
    const { data } = await api.post<Envelope<{ verified: boolean }>>('/auth/verify-email', { token });
    return data.data;
  },

  async resendVerification(): Promise<{ sent: boolean }> {
    const { data } = await api.post<Envelope<{ sent: boolean }>>('/auth/resend-verification');
    return data.data;
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email });
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await api.post('/auth/reset-password', { token, password });
  },
};
